import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ChartContainer } from "../ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { AlertTriangle, ShieldAlert, History, Lock } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useZonePagination } from "../../../hooks/basic-reports/use-meter-pagination";
import MeterPagination from "./MeterPagination";
import ExportButton from "./ExportButton";
import { CHIP_COLORS } from "../utils/colors";
import { useReportData } from "../../../contexts/ReportDataContext";

const chartConfig = {
  events: { label: "Events", color: CHIP_COLORS.failed },
};

const ITEMS_PER_PAGE = 10;

const TamperFraud = () => {
  const { reports, loading, error, progress, dateRange } = useReportData();
  const [showMeterTable, setShowMeterTable] = useState(true);
  const { getPage, setPage } = useZonePagination();

  // Safely extract data for top-level hooks
  const timestamp = reports?.tamper ? new Date().getTime() : 0; // Force re-render if data changes? No, reports object ref changes.
  const dailyTamperTrend = reports?.tamper?.dailyTamperTrend || [];
  const perMeter = reports?.tamper?.perMeter || [];

  // Fill gaps in daily trend based on date range
  const filledDailyTrend = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return dailyTamperTrend;
    const result = [];
    const start = new Date(dateRange.from);
    const end = new Date(dateRange.to);
    // Create a new date instance to avoid infinite loop
    const current = new Date(start);
    while (current <= end) {
      const dateKey = current.toLocaleDateString("en-CA"); // YYYY-MM-DD (local)
      // Match by date key (YYYY-MM-DD) instead of label
      const existing = dailyTamperTrend.find((item) => item.date === dateKey);

      if (existing) {
        result.push(existing);
      } else {
        // Format label to match getDailyAggregation format (e.g., "Mon, Jan 1")
        const label = current.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
        result.push({ label, value: 0 });
      }
      current.setDate(current.getDate() + 1);
    }
    return result;
  }, [dailyTamperTrend, dateRange]);

  console.log("Filled Daily Trend:", JSON.stringify(filledDailyTrend));
  console.log("DateRange:", JSON.stringify(dateRange));

  // Pagination logic (safe with empty perMeter)
  const currentPage = getPage("meters");
  const totalPages = Math.ceil((perMeter.length || 0) / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, perMeter.length);
  const paginatedMeters = perMeter.slice(startIndex, endIndex);

  if (loading)
    return (
      <div className="p-10 text-center">
        <div className="animate-pulse text-muted-foreground">
          {progress || "Loading Report Data..."}
        </div>
      </div>
    );
  if (error)
    return <div className="p-10 text-center text-destructive">{error}</div>;
  if (!reports || !reports.tamper)
    return <div className="p-10 text-center">No data available</div>;

  const { tamper } = reports;
  const { metrics, eventsByType } = tamper;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-destructive/10">
                <ShieldAlert className="w-4 h-4 text-destructive" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Total Tamper Events
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-lg font-bold">
                    {metrics.tamperEvents.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <History className="w-4 h-4 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">MD Resets</p>
                <p className="text-lg font-bold">
                  {metrics.resetEvents.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-info/10">
                <Lock className="w-4 h-4 text-info" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Program Changes</p>
                <p className="text-lg font-bold">
                  {metrics.programChanges.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <AlertTriangle className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Anomalies Detected
                </p>
                <p className="text-lg font-bold">
                  {metrics.anomalies.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-8">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">
                  Daily Tamper Trend
                </CardTitle>
                <CardDescription>
                  Tamper events over the selected period
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                Time Series
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={filledDailyTrend}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    opacity={0.5}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--muted)/0.2)" }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium text-xs mb-2">
                              {payload[0].payload.label}
                            </p>
                            <p className="text-sm font-medium">
                              {payload[0].value} Events
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill={CHIP_COLORS.failed}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="col-span-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Event Breakdown
            </CardTitle>
            <CardDescription>Distribution by event type</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={eventsByType}
                    cx="50%"
                    cy="45%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="type"
                  >
                    {eventsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium">{data.type}</p>
                            <p className="text-sm text-muted-foreground">
                              {data.count} occurrences
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value, entry: any) => (
                      <span className="text-xs text-muted-foreground">
                        {entry.payload.type} ({entry.payload.count})
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Per-Meter Tamper Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">
                Per-Meter Tamper Analysis
              </CardTitle>
              <CardDescription>
                Detailed tamper log for {perMeter.length} meters
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton reportName="Meter Tamper Log" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-primary"
                onClick={() => setShowMeterTable(!showMeterTable)}
              >
                {showMeterTable ? "Hide Details" : "Show Details"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showMeterTable && (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter ID</TableHead>
                  <TableHead className="text-right">Tamper Events</TableHead>
                  <TableHead className="text-right">MD Resets</TableHead>
                  <TableHead className="text-right">Prog. Changes</TableHead>
                  <TableHead className="text-right">Reverse Flow</TableHead>
                  <TableHead className="text-right">Load Violations</TableHead>
                  <TableHead className="text-right">Severity</TableHead>
                  <TableHead className="text-right">Last Event</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMeters.map((meter) => (
                  <TableRow key={meter.meterId} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      {meter.meterId.replace(/^Demo1_/, "")}
                    </TableCell>
                    <TableCell className="text-right text-destructive font-medium">
                      {meter.tamperEvents}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.resetEvents}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.programChanges}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.reverseFlowEvents}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.loadViolations}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        style={{
                          borderColor:
                            CHIP_COLORS[
                              meter.severity as keyof typeof CHIP_COLORS
                            ] || CHIP_COLORS.medium,
                          color:
                            CHIP_COLORS[
                              meter.severity as keyof typeof CHIP_COLORS
                            ] || CHIP_COLORS.medium,
                        }}
                      >
                        {meter.severity.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {meter.lastEvent}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <MeterPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={perMeter.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={(page) => setPage("meters", page)}
              hasNextPage={currentPage < totalPages}
              hasPrevPage={currentPage > 1}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default TamperFraud;
